import { DatePickerProps, DateRange } from '@/shared/types/datepicker';
import {
  dateToParts,
  DEFAULT_TIME_PARTS,
  mergeDateWithTimeParts,
  DateTimeParts,
} from './datetimeFormat';

export interface ResolvedDatePickerTimeConfig {
  /** Panel de hora visible (desde `time.showTime` o `showTime` legacy, por defecto `false`). */
  visible: boolean;
  /** Panel editable cuando `visible` es `true` (desde `time.enabled`, por defecto `true`). */
  enabled: boolean;
  currentTime: boolean;
  showSeconds: boolean;
}

/** Fusiona props agrupadas `time` con la API plana legacy (`showTime`, `showSeconds`). */
export function resolveDatePickerTimeConfig(
  props: Pick<DatePickerProps, 'time' | 'showTime' | 'showSeconds'>
): ResolvedDatePickerTimeConfig {
  const group = props.time ?? {};
  return {
    visible: group.showTime ?? props.showTime ?? false,
    enabled: group.enabled ?? true,
    currentTime: group.currentTime ?? true,
    showSeconds: group.showSeconds ?? props.showSeconds ?? true,
  };
}

export type ActiveRangeEndpoint = 'start' | 'end';

/** Extremo del rango que edita el TimePickerPanel según el estado de selección. */
export function getActiveRangeEndpoint(range: DateRange): ActiveRangeEndpoint {
  if (range.start && range.end) {
    return 'end';
  }
  return 'start';
}

export function getTimePickerValueForRange(range: DateRange): Date | null {
  const endpoint = getActiveRangeEndpoint(range);
  return endpoint === 'end' ? range.end : range.start;
}

type TimeParts = Pick<DateTimeParts, 'hour12' | 'minute' | 'second' | 'meridiem'>;

function freshTimeParts(currentTime: boolean, showSeconds: boolean): TimeParts {
  return currentTime ? dateToParts(new Date(), showSeconds) : DEFAULT_TIME_PARTS;
}

/**
 * Resuelve las partes de hora al seleccionar un día en modo range con hora activa.
 */
export function resolveRangeEndpointTimeParts(
  selectedRange: DateRange,
  pendingRange: DateRange,
  currentTime: boolean,
  showSeconds: boolean
): TimeParts {
  const isRestarting = !!(selectedRange.start && selectedRange.end);
  const isSelectingEnd = !!(selectedRange.start && !selectedRange.end);

  if (isRestarting) {
    return freshTimeParts(currentTime, showSeconds);
  }

  if (isSelectingEnd) {
    const existingEnd = pendingRange.end ?? selectedRange.end;
    if (existingEnd) {
      return dateToParts(existingEnd, showSeconds);
    }
    return freshTimeParts(currentTime, showSeconds);
  }

  const existingStart = pendingRange.start ?? selectedRange.start;
  if (existingStart) {
    return dateToParts(existingStart, showSeconds);
  }

  return freshTimeParts(currentTime, showSeconds);
}

/** Combina un día de calendario con las partes de hora resueltas para un extremo del rango. */
export function mergeCalendarDayWithRangeTime(
  day: Date,
  selectedRange: DateRange,
  pendingRange: DateRange,
  currentTime: boolean,
  showSeconds: boolean
): Date {
  const timeParts = resolveRangeEndpointTimeParts(
    selectedRange,
    pendingRange,
    currentTime,
    showSeconds
  );
  return mergeDateWithTimeParts(day, timeParts, showSeconds);
}
